import { EmptyState } from "@/components/EmptyState";
import { LoadingPanel } from "@/components/LoadingPanel";
import { RepairComposer } from "@/components/RepairComposer";
import { RepairThread } from "@/components/RepairThread";
import { RepairTimeline } from "@/components/RepairTimeline";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  REPAIR_PRIORITY_LABEL,
  REPAIR_STATUS_LABEL,
  useAdminRepairs,
  useUpdateRepairStatus,
} from "@/hooks/useRepairs";
import { cn } from "@/lib/cn";
import { formatDateGB } from "@/lib/format";
import {
  type Contractor,
  HMF_TRADES,
  type RepairAdmin,
  type RepairStatus,
  getSupabase,
} from "@/lib/supabase";
import { ExternalLink, FileText, Phone, UserPlus, XCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const STATUS_ORDER: RepairStatus[] = [
  "reported",
  "triaged",
  "contractor_assigned",
  "in_progress",
  "completed",
  "cancelled",
];
const STATUS_BADGE: Record<RepairStatus, string> = {
  reported: "bg-white/5 text-ink-dim border-line",
  triaged: "bg-blue-500/15 text-blue-200 border-blue-500/30",
  contractor_assigned: "bg-amber-500/15 text-amber-200 border-amber-500/30",
  in_progress: "bg-yellow-500/15 text-yellow-200 border-yellow-500/30",
  completed: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  cancelled: "bg-red-500/15 text-red-200 border-red-500/30",
};

export function AdminRepairs() {
  const { repairs, loading, error, refresh } = useAdminRepairs();
  const [filter, setFilter] = useState<"all" | RepairStatus>("all");
  const [editing, setEditing] = useState<RepairAdmin | null>(null);

  const filtered = useMemo(
    () => (filter === "all" ? repairs : repairs.filter((r) => r.status === filter)),
    [repairs, filter],
  );

  if (loading) return <LoadingPanel />;

  return (
    <div className="flex flex-col gap-4">
      {error ? (
        <Card>
          <CardContent className="py-6">
            <p role="alert" className="text-sm text-red-300">
              {error}
            </p>
          </CardContent>
        </Card>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {(["all", ...STATUS_ORDER] as Array<"all" | RepairStatus>).map((s) => (
          <button
            type="button"
            key={s}
            onClick={() => setFilter(s)}
            className={cn(
              "px-3 h-8 rounded-full text-xs uppercase tracking-wider border transition whitespace-nowrap",
              filter === s
                ? "bg-accent text-bg border-accent"
                : "border-line text-ink-dim hover:text-ink",
            )}
          >
            {s === "all" ? "All" : REPAIR_STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="No repairs match"
          description="Pick another filter or wait for tenants to report something."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y divide-line">
              {filtered.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setEditing(r)}
                    className="w-full text-left px-6 py-4 hover:bg-white/5 transition flex items-start gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{r.title}</span>
                        <StatusPill status={r.status} />
                      </div>
                      <p className="text-xs text-ink-dim mt-1">
                        Reported {formatDateGB(r.created_at)} · Priority{" "}
                        {REPAIR_PRIORITY_LABEL[r.reporter_priority]}
                      </p>
                      <p className="text-sm text-ink-dim mt-2 line-clamp-2">{r.description}</p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {editing ? (
        <RepairEditor
          repair={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      ) : null}
    </div>
  );
}

type HmfCall = {
  id: string;
  hmf_session_id: number;
  hmf_call_id: number;
  service: string | null;
  call_start: string | null;
  call_end: string | null;
  engineer_first_name: string | null;
  engineer_last_name: string | null;
  engineer_phone: string | null;
  engineer_email: string | null;
  worker_report: string | null;
  further_work_required: boolean | null;
  further_work_details: string | null;
  issue_resolved_status: string | null;
  emergency_dispatch_required: boolean | null;
  is_vulnerable: boolean | null;
  photos: string[] | null;
  photos_archive: string[] | null;
  recording_url: string | null;
  recording_archive_path: string | null;
  report_url: string | null;
  report_archive_path: string | null;
  secure_report_url: string | null;
  secure_report_password: string | null;
  duration_seconds: number | null;
  cost_pence: number | null;
  hmf_status: string | null;
  archived_at: string | null;
};

const ARCHIVE_SIGN_TTL = 60 * 60; // 1 hour

function HmfCallsSection({
  repairId,
  fallbackSummary,
}: {
  repairId: string;
  fallbackSummary: string | null;
}) {
  const [calls, setCalls] = useState<HmfCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [archiveUrls, setArchiveUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sb = getSupabase();
      const { data } = await sb
        .from("repair_calls")
        .select(
          "id, hmf_session_id, hmf_call_id, service, call_start, call_end, engineer_first_name, engineer_last_name, engineer_phone, engineer_email, worker_report, further_work_required, further_work_details, issue_resolved_status, emergency_dispatch_required, is_vulnerable, photos, photos_archive, recording_url, recording_archive_path, report_url, report_archive_path, secure_report_url, secure_report_password, duration_seconds, cost_pence, hmf_status, archived_at",
        )
        .eq("repair_id", repairId)
        .order("call_start", { ascending: true });
      const rows = (data as unknown as HmfCall[] | null) ?? [];
      if (cancelled) return;
      setCalls(rows);
      setLoading(false);

      // Batch-sign every archive path so video + thumbnails render straight from
      // our private bucket (no public URLs leaving Trinium).
      const paths = rows.flatMap((c) =>
        [
          c.recording_archive_path,
          c.report_archive_path,
          ...((c.photos_archive ?? []) as string[]),
        ].filter((p): p is string => !!p),
      );
      if (paths.length === 0) return;
      const next: Record<string, string> = {};
      await Promise.all(
        paths.map(async (p) => {
          const { data: signed } = await sb.storage
            .from("hmf-archive")
            .createSignedUrl(p, ARCHIVE_SIGN_TTL);
          if (signed?.signedUrl) next[p] = signed.signedUrl;
        }),
      );
      if (!cancelled) setArchiveUrls(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [repairId]);

  if (loading) return null;
  const firstCall = calls[0];

  // No HMF call yet — show AI triage summary alone if we have one. If neither,
  // hide the card entirely.
  if (!firstCall) {
    if (!fallbackSummary) return null;
    return (
      <Card>
        <CardHeader>
          <CardTitle>Triage summary</CardTitle>
          <CardDescription>From the AI triage chat — no engineer call yet.</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm whitespace-pre-wrap">{fallbackSummary}</p>
        </CardContent>
      </Card>
    );
  }

  const sessionId = firstCall.hmf_session_id;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Triage report{calls.length === 1 ? "" : "s"}</CardTitle>
        <CardDescription>
          Video triage with a Help me Fix engineer · session{" "}
          <span className="font-mono">{sessionId}</span> · {calls.length} call
          {calls.length === 1 ? "" : "s"}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {calls.map((c, idx) => {
          const recordingSrc = c.recording_archive_path
            ? (archiveUrls[c.recording_archive_path] ?? null)
            : c.recording_url;
          const photoSources =
            c.photos_archive && c.photos_archive.length > 0
              ? c.photos_archive.map((p) => archiveUrls[p]).filter((u): u is string => !!u)
              : (c.photos ?? []);
          const reportArchiveSigned = c.report_archive_path
            ? (archiveUrls[c.report_archive_path] ?? null)
            : null;
          return (
            <div key={c.id} className="flex flex-col gap-3">
              {idx > 0 ? <hr className="border-line" /> : null}
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <span className="px-2 py-0.5 rounded-full bg-bg-card border border-line text-ink-dim">
                  Call <span className="font-mono">{c.hmf_call_id}</span>
                </span>
                {c.service ? (
                  <span className="px-2 py-0.5 rounded-full bg-bg-card border border-line text-ink-dim">
                    {c.service}
                  </span>
                ) : null}
                {c.emergency_dispatch_required ? (
                  <span className="px-2 py-0.5 rounded-full bg-red-500/15 border border-red-500/30 text-red-200 uppercase tracking-wider">
                    Emergency
                  </span>
                ) : null}
                {c.further_work_required ? (
                  <span className="px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-200 uppercase tracking-wider">
                    Further work
                  </span>
                ) : null}
                {c.issue_resolved_status ? (
                  <span className="px-2 py-0.5 rounded-full bg-bg-card border border-line text-ink-dim">
                    {c.issue_resolved_status}
                  </span>
                ) : null}
                {c.duration_seconds != null ? (
                  <span className="px-2 py-0.5 rounded-full bg-bg-card border border-line text-ink-dim">
                    {Math.floor(c.duration_seconds / 60)}m {c.duration_seconds % 60}s
                  </span>
                ) : null}
                {c.cost_pence != null ? (
                  <span className="px-2 py-0.5 rounded-full bg-bg-card border border-line text-ink-dim">
                    £{(c.cost_pence / 100).toFixed(2)}
                  </span>
                ) : null}
              </div>

              <div className="text-xs text-ink-dim">
                {[c.engineer_first_name, c.engineer_last_name].filter(Boolean).join(" ") ||
                  "Help me Fix engineer"}
                {c.call_start ? ` · ${formatDateGB(c.call_start)}` : ""}
                {c.engineer_phone ? (
                  <>
                    {" "}
                    ·{" "}
                    <a
                      href={`tel:${c.engineer_phone}`}
                      className="text-accent inline-flex items-center gap-1"
                    >
                      <Phone size={11} /> {c.engineer_phone}
                    </a>
                  </>
                ) : null}
                {c.archived_at ? <span className="ml-2">· archived</span> : null}
              </div>

              {recordingSrc ? (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-ink-dim mb-2">
                    Call recording
                  </p>
                  <video
                    controls
                    preload="metadata"
                    src={recordingSrc}
                    poster={photoSources[0] ?? undefined}
                    className="w-full max-h-[480px] rounded-lg border border-line bg-black"
                  >
                    <track kind="captions" />
                  </video>
                </div>
              ) : null}

              {c.worker_report ? (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-ink-dim mb-1">
                    Engineer report
                  </p>
                  <p className="text-sm whitespace-pre-wrap">{c.worker_report}</p>
                </div>
              ) : null}

              {c.further_work_details ? (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-ink-dim mb-1">
                    Further work required
                  </p>
                  <p className="text-sm whitespace-pre-wrap">{c.further_work_details}</p>
                </div>
              ) : null}

              {photoSources.length > 0 ? (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-ink-dim mb-2">
                    Photos ({photoSources.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {photoSources.map((p) => (
                      <a
                        key={p}
                        href={p}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-20 h-20 rounded-lg overflow-hidden border border-line bg-bg-card"
                      >
                        <img src={p} alt="Triage" className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="flex flex-wrap gap-2">
                {c.secure_report_url ? (
                  <Button asChild size="sm" variant="secondary">
                    <a href={c.secure_report_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink size={14} /> Secure report
                    </a>
                  </Button>
                ) : null}
                {reportArchiveSigned ? (
                  <Button asChild size="sm" variant="ghost">
                    <a href={reportArchiveSigned} target="_blank" rel="noopener noreferrer">
                      <FileText size={14} /> Report PDF (archived)
                    </a>
                  </Button>
                ) : c.report_url ? (
                  <Button asChild size="sm" variant="ghost">
                    <a href={c.report_url} target="_blank" rel="noopener noreferrer">
                      <FileText size={14} /> Report PDF
                    </a>
                  </Button>
                ) : null}
              </div>
              {c.secure_report_password ? (
                <p className="text-xs text-ink-dim">
                  Secure report password:{" "}
                  <code className="font-mono">{c.secure_report_password}</code>
                </p>
              ) : null}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function StatusPill({ status }: { status: RepairStatus }) {
  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wider border",
        STATUS_BADGE[status],
      )}
    >
      {REPAIR_STATUS_LABEL[status]}
    </span>
  );
}

function RepairEditor({
  repair,
  onClose,
  onSaved,
}: {
  repair: RepairAdmin;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { update, submitting, error } = useUpdateRepairStatus();
  const [status, setStatus] = useState<RepairStatus>(repair.status);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [selectedContractorId, setSelectedContractorId] = useState<string | null>(
    repair.contractor_id ?? null,
  );
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(repair.scheduled_at?.slice(0, 16) ?? "");
  const [completionNotes, setCompletionNotes] = useState(repair.completion_notes ?? "");
  const [internalNotes, setInternalNotes] = useState(repair.internal_notes ?? "");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sb = getSupabase();
      const { data } = await sb.from("contractors").select("*").order("name");
      if (!cancelled) setContractors((data as Contractor[] | null) ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedContractor = contractors.find((c) => c.id === selectedContractorId) ?? null;

  async function save() {
    const newContractorIdChanged = selectedContractorId !== (repair.contractor_id ?? null);
    let nextStatus = status;
    if (newContractorIdChanged && selectedContractorId) {
      // Manual assign / re-assign — bump status to "contractor_assigned" unless
      // we're already further along the lifecycle.
      if (status === "reported" || status === "triaged") {
        nextStatus = "contractor_assigned";
        setStatus("contractor_assigned");
      }
    }

    const updates: Partial<RepairAdmin> = {
      status: nextStatus,
      contractor_id: selectedContractorId,
      contractor_name: selectedContractor?.name ?? null,
      contractor_phone: selectedContractor?.phone ?? null,
      contractor_email: selectedContractor?.email ?? null,
      scheduled_at: scheduledAt === "" ? null : new Date(scheduledAt).toISOString(),
      completion_notes: completionNotes.trim() === "" ? null : completionNotes.trim(),
      internal_notes: internalNotes.trim() === "" ? null : internalNotes.trim(),
    };
    if (newContractorIdChanged && selectedContractorId && !repair.contractor_dispatched_at) {
      updates.contractor_dispatched_at = new Date().toISOString();
    }
    if (status === "triaged" && !repair.triaged_at) {
      updates.triaged_at = new Date().toISOString();
    }
    if (status === "completed" && !repair.completed_at) {
      updates.completed_at = new Date().toISOString();
    }
    const err = await update(repair.id, updates);
    if (err) return;
    // Manual assign — fire the dispatch agent so the contractor gets the
    // work-instruction email (same path the auto-dispatcher takes off the
    // back of HMF further-work webhooks).
    if (newContractorIdChanged && selectedContractorId) {
      const sb = getSupabase();
      const { data: session } = await sb.auth.getSession();
      const token = session.session?.access_token;
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/dispatch-contractor`;
      void fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ repair_id: repair.id }),
      }).catch(() => {});
    }
    onSaved();
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-stretch justify-end"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <aside
        className="bg-bg w-full max-w-2xl h-full overflow-y-auto border-l border-line"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-line flex items-start justify-between gap-4">
          <div>
            <span className="text-xs uppercase tracking-[0.18em] text-ink-dim">Repair</span>
            <h2 className="text-xl font-semibold mt-1">{repair.title}</h2>
            <p className="text-xs text-ink-dim mt-1">
              Reported {formatDateGB(repair.created_at)} · Priority{" "}
              {REPAIR_PRIORITY_LABEL[repair.reporter_priority]}
            </p>
          </div>
          <button type="button" className="text-ink-dim hover:text-ink" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="px-6 py-5 flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Lifecycle</CardTitle>
            </CardHeader>
            <CardContent>
              <RepairTimelineWrapper repairId={repair.id} status={repair.status} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Tenant report</CardTitle>
              <CardDescription>Read-only — submitted by the tenant.</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm whitespace-pre-wrap">{repair.description}</p>
            </CardContent>
          </Card>

          <TenantChatSection repairId={repair.id} />

          <RepairThread repairId={repair.id} />
          {repair.status !== "completed" && repair.status !== "cancelled" ? (
            <RepairComposer repairId={repair.id} />
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Status</CardTitle>
              <CardDescription>Changing status emits an event on the timeline.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <Field id="repair-status" label="Status">
                <select
                  id="repair-status"
                  className="h-10 px-3 rounded-md border border-line bg-transparent text-ink"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as RepairStatus)}
                >
                  {STATUS_ORDER.map((s) => (
                    <option key={s} value={s}>
                      {REPAIR_STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
              </Field>
            </CardContent>
          </Card>

          <HmfCallsSection repairId={repair.id} fallbackSummary={repair.triage_summary} />

          <Card>
            <CardHeader>
              <CardTitle>Contractor</CardTitle>
              <CardDescription>
                Pick from your active contractors, or quick-add a new one. Picking a contractor and
                saving instructs them and bumps the repair to "Contractor assigned".
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <Field id="contractor-pick" label="Assigned contractor">
                <select
                  id="contractor-pick"
                  className="h-10 px-3 rounded-md border border-line bg-transparent text-ink"
                  value={selectedContractorId ?? ""}
                  onChange={(e) => setSelectedContractorId(e.target.value || null)}
                >
                  <option value="">— None —</option>
                  {contractors.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.is_active ? c.name : `${c.name} (inactive)`}
                      {c.trades.length
                        ? ` · ${c.trades.slice(0, 3).join("/")}${c.trades.length > 3 ? "+" : ""}`
                        : ""}
                    </option>
                  ))}
                </select>
              </Field>

              {selectedContractor ? (
                <div className="rounded-xl border border-line bg-bg-card p-3 text-sm flex flex-col gap-1">
                  <div className="font-medium text-ink">{selectedContractor.name}</div>
                  {selectedContractor.email ? (
                    <a href={`mailto:${selectedContractor.email}`} className="text-accent">
                      {selectedContractor.email}
                    </a>
                  ) : null}
                  {selectedContractor.phone ? (
                    <a href={`tel:${selectedContractor.phone}`} className="text-accent">
                      {selectedContractor.phone}
                    </a>
                  ) : null}
                  {selectedContractor.trades.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedContractor.trades.map((t) => (
                        <span
                          key={t}
                          className="text-[11px] px-2 py-0.5 rounded-full bg-bg border border-line text-ink-dim"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowQuickAdd((v) => !v)}
                >
                  <UserPlus size={14} />{" "}
                  {showQuickAdd ? "Cancel quick add" : "Quick add new contractor"}
                </Button>
              </div>

              {showQuickAdd ? (
                <QuickAddContractor
                  onCreated={(c) => {
                    setContractors((prev) =>
                      [...prev, c].sort((a, b) => a.name.localeCompare(b.name)),
                    );
                    setSelectedContractorId(c.id);
                    setShowQuickAdd(false);
                  }}
                />
              ) : null}

              <Field id="scheduled-at" label="Scheduled">
                <Input
                  id="scheduled-at"
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                />
              </Field>

              {repair.contractor_id && repair.status === "contractor_assigned" ? (
                <WithdrawInstructionBlock
                  repairId={repair.id}
                  contractorName={
                    repair.contractor_name ?? selectedContractor?.name ?? "the contractor"
                  }
                  onWithdrawn={onSaved}
                />
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Completion + notes</CardTitle>
              <CardDescription>
                Completion notes show on the tenant detail. Internal notes never do.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <Field id="completion-notes" label="Completion notes (visible to tenant)">
                <textarea
                  id="completion-notes"
                  className="min-h-[80px] px-3 py-2 rounded-md border border-line bg-transparent text-ink"
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                />
              </Field>
              <Field id="internal-notes" label="Internal notes (admin only)">
                <textarea
                  id="internal-notes"
                  className="min-h-[80px] px-3 py-2 rounded-md border border-line bg-transparent text-ink"
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                />
              </Field>
            </CardContent>
          </Card>

          {error ? (
            <p role="alert" className="text-sm text-red-300">
              {error}
            </p>
          ) : null}
          <div className="flex gap-3">
            <Button onClick={save} disabled={submitting}>
              {submitting ? "Saving…" : "Save"}
            </Button>
            <Button variant="ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function RepairTimelineWrapper({ repairId, status }: { repairId: string; status: RepairStatus }) {
  const [hadVideoTriage, setHadVideoTriage] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { count } = await getSupabase()
        .from("repair_calls")
        .select("id", { count: "exact", head: true })
        .eq("repair_id", repairId);
      if (!cancelled) {
        setHadVideoTriage((count ?? 0) > 0);
        setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [repairId]);

  if (!loaded) return null;
  return <RepairTimeline status={status} hadVideoTriage={hadVideoTriage} />;
}

type ChatRow = {
  id: string;
  session_id: string;
  role: "user" | "assistant" | "tool" | "system";
  content: string | null;
  attachments: Array<{ path: string; mime?: string }> | null;
  created_at: string;
};

function TenantChatSection({ repairId }: { repairId: string }) {
  const [messages, setMessages] = useState<ChatRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [attachUrls, setAttachUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sb = getSupabase();
      // Pull the chat_session_id explicitly from the repair (set by tenant-chat
      // when escalate_to_video_triage / log_repair fires). Repairs not created
      // via chat (admin manual, future inbound channels) leave it null and we
      // render nothing — no guessing by tenant+property+latest, which used to
      // leak unrelated chat transcripts onto unrelated repairs.
      const { data: repairRow } = await sb
        .from("repairs")
        .select("chat_session_id")
        .eq("id", repairId)
        .maybeSingle();
      if (cancelled) return;
      const sessionId =
        (repairRow as { chat_session_id?: string | null } | null)?.chat_session_id ?? null;
      if (!sessionId) {
        setLoading(false);
        return;
      }
      const { data: msgs } = await sb
        .from("chat_messages")
        .select("id, session_id, role, content, attachments, created_at")
        .eq("session_id", sessionId)
        .order("created_at", { ascending: true });
      const rows = (msgs as unknown as ChatRow[] | null) ?? [];
      // Filter out tool messages from display — they're internal noise.
      const display = rows.filter((m) => m.role === "user" || m.role === "assistant");
      setMessages(display);
      setLoading(false);

      // Sign attachment paths from chat-attachments bucket.
      const paths = display.flatMap((m) =>
        Array.isArray(m.attachments) ? m.attachments.map((a) => a.path) : [],
      );
      if (paths.length === 0) return;
      const next: Record<string, string> = {};
      await Promise.all(
        paths.map(async (p) => {
          const { data: signed } = await sb.storage
            .from("chat-attachments")
            .createSignedUrl(p, 60 * 60);
          if (signed?.signedUrl) next[p] = signed.signedUrl;
        }),
      );
      if (!cancelled) setAttachUrls(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [repairId]);

  if (loading) return null;
  if (messages.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>AI triage chat</CardTitle>
        <CardDescription>
          Transcript of the tenant's conversation with the Trinium triage assistant.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ol className="flex flex-col gap-3">
          {messages.map((m) => {
            const isUser = m.role === "user";
            return (
              <li key={m.id} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ${
                    isUser
                      ? "bg-accent/15 text-ink border border-accent/30"
                      : "bg-bg-card text-ink border border-line"
                  }`}
                >
                  <p className="text-[10px] uppercase tracking-wider text-ink-dim mb-1">
                    {isUser ? "Tenant" : "Trinium"} · {formatDateGB(m.created_at)}
                  </p>
                  {m.content ? <p className="whitespace-pre-wrap">{m.content}</p> : null}
                  {Array.isArray(m.attachments) && m.attachments.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {m.attachments.map((a) => {
                        const url = attachUrls[a.path];
                        if (!url) return null;
                        return (
                          <a
                            key={a.path}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-24 h-24 rounded-lg overflow-hidden border border-line bg-bg"
                          >
                            <img
                              src={url}
                              alt="Tenant attachment"
                              className="w-full h-full object-cover"
                            />
                          </a>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

function QuickAddContractor({ onCreated }: { onCreated: (c: Contractor) => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [trades, setTrades] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function toggleTrade(t: string) {
    setTrades((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  }

  async function submit() {
    setErr(null);
    if (!name.trim()) {
      setErr("Name required");
      return;
    }
    if (!email.trim() && !phone.trim()) {
      setErr("Email or phone required");
      return;
    }
    if (trades.length === 0) {
      setErr("Pick at least one trade");
      return;
    }
    setSubmitting(true);
    const sb = getSupabase();
    const { data, error: insErr } = await sb
      .from("contractors")
      .insert({
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        trades,
        is_active: true,
      })
      .select("*")
      .single();
    setSubmitting(false);
    if (insErr || !data) {
      setErr(insErr?.message ?? "Could not create contractor");
      return;
    }
    onCreated(data as Contractor);
  }

  return (
    <div className="rounded-xl border border-line bg-bg-card p-3 flex flex-col gap-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Input
          placeholder="Company / contact name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Input
          placeholder="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-ink-dim mb-2">Trades</p>
        <div className="flex flex-wrap gap-1.5">
          {HMF_TRADES.map((t) => {
            const on = trades.includes(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => toggleTrade(t)}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                  on
                    ? "bg-accent text-accent-on border-accent"
                    : "bg-bg text-ink-dim border-line hover:text-ink"
                }`}
              >
                {t}
              </button>
            );
          })}
        </div>
      </div>
      {err ? (
        <p role="alert" className="text-xs text-red-300">
          {err}
        </p>
      ) : null}
      <div>
        <Button type="button" size="sm" onClick={submit} disabled={submitting}>
          {submitting ? "Creating…" : "Create + assign"}
        </Button>
      </div>
    </div>
  );
}

function WithdrawInstructionBlock({
  repairId,
  contractorName,
  onWithdrawn,
}: {
  repairId: string;
  contractorName: string;
  onWithdrawn: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function withdraw() {
    setErr(null);
    setSubmitting(true);
    try {
      const sb = getSupabase();
      const { data: session } = await sb.auth.getSession();
      const token = session.session?.access_token;
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/withdraw-instruction`;
      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ repair_id: repairId, reason: reason.trim() || undefined }),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        setErr(t.slice(0, 200) || `Withdraw failed (${res.status})`);
        return;
      }
      onWithdrawn();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-3 flex flex-col gap-3">
      <div>
        <div className="text-sm font-medium text-red-200">Withdraw instruction</div>
        <div className="text-xs text-red-200/70 mt-1">
          Sends a cancellation email to {contractorName} and reverts this repair to Triaged so you
          can re-assign.
        </div>
      </div>
      {open ? (
        <>
          <Field id="withdraw-reason" label="Reason (optional, included in the email)">
            <textarea
              id="withdraw-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-line bg-bg-card p-2.5 text-sm text-ink"
              placeholder="e.g. Tenant rebooked the appointment, going with another contractor"
            />
          </Field>
          {err ? (
            <p role="alert" className="text-xs text-red-300">
              {err}
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={withdraw} disabled={submitting}>
              <XCircle size={14} /> {submitting ? "Withdrawing…" : "Confirm withdraw"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <div>
          <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(true)}>
            <XCircle size={14} /> Withdraw instruction…
          </Button>
        </div>
      )}
    </div>
  );
}
